import React, { useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Progress,
  SimpleGrid,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { DateTime } from "luxon";
import EmployeeDataService from "../../services/Employee";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { HrmsTable } from "../../components/Hrms/HrmsTable";
import { HrmsStatCard } from "../../components/Hrms/HrmsStatCard";
import { getUserAccess } from "../../utils/authUtils";

interface LeaveTypeBalance {
  annual: number;
  used: number;
  remaining: number;
}

interface EmployeeLeaveBalance {
  email: string;
  name?: string;
  employmentStatus?: string;
  leaveCycleStartDate?: string;
  cycleStart: string | null;
  cycleEnd: string | null;
  balances: Record<string, LeaveTypeBalance>;
  eligibility?: Record<string, { eligible: boolean; reason: string }>;
}

interface LeaveTypeOption {
  code: string;
  name: string;
}

// Column order for the usual types; any other active type follows.
const TYPE_ORDER = ["casual", "sick", "exigency", "optional", "compensatory", "unpaid"];
// Same colours as the Leave Policy cards.
const TYPE_COLOR: Record<string, string> = {
  casual: "teal",
  sick: "red",
  exigency: "orange",
  optional: "purple",
  compensatory: "blue",
  unpaid: "gray",
};
// "Running low" = this few casual days or fewer left.
const LOW_CASUAL_DAYS = 2;

const shortDate = (iso: string) => DateTime.fromISO(iso).toFormat("dd LLL yy");

const leaveYearLabel = (row: EmployeeLeaveBalance) => {
  if (row.cycleStart && row.cycleEnd)
    return `${shortDate(row.cycleStart)} – ${shortDate(row.cycleEnd)}`;
  return row.leaveCycleStartDate ? `Starts ${shortDate(row.leaveCycleStartDate)}` : "";
};

/**
 * Team Leave Balance: a Manager sees their direct reports; Admin/HR
 * (attendance.view.all) see every current employee. The backend applies the
 * scope. Employees see their own balance on My Leaves instead.
 */
export const LeaveBalance = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const { canViewTeam, canViewAllEmployees } = getUserAccess();
  const canViewTeamBalance = canViewTeam || canViewAllEmployees;

  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<EmployeeLeaveBalance[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeOption[]>([]);
  const [search, setSearch] = useState("");

  const fetchBalances = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getTeamLeaveTypeBalance(token, userEmail);
      setRows(res.data.status === "success" ? res.data.data || [] : []);
    } catch (e: any) {
      console.error("Error fetching leave balances:", e);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    EmployeeDataService.getLeaveTypes(token)
      .then((res) => {
        if (res.data.status === "success") setLeaveTypes(res.data.data || []);
      })
      .catch((e) => console.error("Error fetching leave types:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (canViewTeamBalance) fetchBalances();
    else setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leaveTypeName = (code: string) =>
    leaveTypes.find((lt) => lt.code === code)?.name || code;

  // One column per leave type, so each employee is a single row.
  const typeCodes = Array.from(
    new Set(rows.flatMap((row) => Object.keys(row.balances || {}))),
  ).sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a);
    const bi = TYPE_ORDER.indexOf(b);
    return (ai === -1 ? TYPE_ORDER.length : ai) - (bi === -1 ? TYPE_ORDER.length : bi);
  });

  const query = search.trim().toLowerCase();
  const visibleRows = query
    ? rows.filter(
        (row) =>
          (row.name || "").toLowerCase().includes(query) ||
          row.email.toLowerCase().includes(query),
      )
    : rows;

  const isEligibleFor = (row: EmployeeLeaveBalance, code: string) =>
    row.eligibility?.[code]?.eligible !== false;
  const daysTaken = rows.reduce(
    (sum, row) =>
      sum +
      Object.entries(row.balances || {})
        .filter(([code]) => code !== "unpaid")
        .reduce((s, [, b]) => s + (b.used || 0), 0),
    0,
  );
  const runningLow = rows.filter((row) => {
    const casual = row.balances?.casual;
    return (
      casual &&
      casual.remaining !== -1 &&
      isEligibleFor(row, "casual") &&
      casual.remaining <= LOW_CASUAL_DAYS
    );
  }).length;
  const onProbation = rows.filter((row) => row.employmentStatus === "probation").length;

  const renderBalance = (row: EmployeeLeaveBalance, code: string) => {
    const balance = row.balances?.[code];
    if (!balance) return <Text color="gray.300" m={0}>–</Text>;

    const eligibility = row.eligibility?.[code];
    if (eligibility && !eligibility.eligible) {
      return (
        <Tooltip label={eligibility.reason} hasArrow>
          <Text as="span" fontSize="12px" color="gray.400">
            Not eligible
          </Text>
        </Tooltip>
      );
    }

    // remaining/annual of -1 means the type has no cap (e.g. unpaid leave).
    if (balance.remaining === -1) {
      return (
        <Box>
          <Text fontSize="13px" fontWeight="600" m={0}>
            No limit
          </Text>
          <Text fontSize="11px" color="gray.500" m={0}>
            {balance.used} used
          </Text>
        </Box>
      );
    }

    // Earned-only types (e.g. comp off) have no yearly allowance.
    if (balance.annual === 0) {
      return (
        <Text fontSize="12px" color="gray.400" m={0}>
          None earned
        </Text>
      );
    }

    const color = TYPE_COLOR[code] || "gray";
    return (
      <Box minW="90px">
        <Text
          fontSize="13px"
          fontWeight="700"
          color={balance.remaining === 0 ? "red.500" : undefined}
          m={0}
        >
          {balance.remaining}
          <Text as="span" fontWeight="500" color="gray.500">
            {" "}
            / {balance.annual} left
          </Text>
        </Text>
        <Progress
          value={balance.annual > 0 ? (balance.used / balance.annual) * 100 : 0}
          size="xs"
          borderRadius="full"
          colorScheme={balance.remaining === 0 ? "red" : color}
          mt={1}
        />
      </Box>
    );
  };

  return (
    <HrmsPageShell
      title="Team Leave Balance"
      sectionKey="leave"
      noPermission={!canViewTeamBalance}
      isLoading={isLoading}
      isEmpty={!isLoading && rows.length === 0}
      emptyText={
        canViewAllEmployees ? "No employees found." : "No direct reports found."
      }
    >
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
        <HrmsStatCard
          label={canViewAllEmployees ? "Employees" : "Team members"}
          value={rows.length}
          helpText={canViewAllEmployees ? "Whole company" : "Your direct reports"}
        />
        <HrmsStatCard
          label="Days taken"
          value={daysTaken}
          helpText="Paid leave, this leave year"
        />
        <HrmsStatCard
          label="Running low"
          value={runningLow}
          accent={runningLow ? "#dd6b20" : undefined}
          helpText={`≤ ${LOW_CASUAL_DAYS} casual days left`}
        />
        <HrmsStatCard
          label="On probation"
          value={onProbation}
          accent={onProbation ? "#dd6b20" : undefined}
          helpText="Some leave types not yet available"
        />
      </SimpleGrid>

      <Flex justify="space-between" align="center" gap={3} mb={3} wrap="wrap">
        <InputGroup maxW={{ base: "100%", md: "300px" }} size="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            bg="white"
            borderRadius="8px"
          />
        </InputGroup>
        <Text fontSize="12px" color="gray.500" m={0}>
          Showing {visibleRows.length} of {rows.length}
        </Text>
      </Flex>

      <HrmsTable
        head={[
          "Employee",
          ...typeCodes.map((code) => (
            <Flex key={code} align="center" gap={1.5} whiteSpace="nowrap">
              <Box
                w="8px"
                h="8px"
                borderRadius="full"
                bg={`${TYPE_COLOR[code] || "gray"}.400`}
              />
              {leaveTypeName(code)}
            </Flex>
          )),
        ]}
        isEmpty={visibleRows.length === 0}
        emptyText="No one matches your search."
      >
        {visibleRows.map((row) => (
          <tr key={row.email}>
            <td>
              <Flex align="center" gap={3} minW="220px">
                <Avatar
                  size="sm"
                  name={row.name || row.email}
                  bg="#e8f3f3"
                  color="#327171"
                />
                <Box minW={0}>
                  <Flex align="center" gap={2}>
                    <Text fontSize="13px" fontWeight="600" m={0} noOfLines={1}>
                      {row.name || row.email}
                    </Text>
                    {row.employmentStatus === "probation" && (
                      <Badge colorScheme="orange" borderRadius="full" px={2} fontSize="9px">
                        Probation
                      </Badge>
                    )}
                  </Flex>
                  <Text fontSize="11px" color="gray.500" m={0} noOfLines={1}>
                    {leaveYearLabel(row) || row.email}
                  </Text>
                </Box>
              </Flex>
            </td>
            {typeCodes.map((code) => (
              <td key={code}>{renderBalance(row, code)}</td>
            ))}
          </tr>
        ))}
      </HrmsTable>
    </HrmsPageShell>
  );
};
